import { useAdminAuth } from '../contexts/AdminAuthContext'
import { API_BASE_URL } from '../config'
import { Feedback, FeedbackStatus } from './feedbackService'

export interface AdminTenant {
  id: string;
  email: string;
  createdAt: string;
  companyName?: string;
  merchantId?: string;
  merchantType?: string;
  planKey?: string;
}

export const useAdminService = () => {
  const { token } = useAdminAuth()

  const authHeaders = () => ({ 'Authorization': `Bearer ${token}` })

  const fetchTenants = async (): Promise<AdminTenant[]> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await fetch(`${API_BASE_URL}/admin/tenants`, { headers: authHeaders() })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to fetch tenants: ${response.status}`)
    }

    return data.tenants
  }

  const fetchFeedback = async (): Promise<Feedback[]> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await fetch(`${API_BASE_URL}/admin/feedback`, { headers: authHeaders() })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to fetch feedback: ${response.status}`)
    }

    return data.feedback
  }

  const updateFeedback = async (feedbackId: string, edit: { status?: FeedbackStatus; adminNotes?: string }): Promise<Feedback> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await fetch(`${API_BASE_URL}/admin/feedback/${feedbackId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(edit),
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to update feedback: ${response.status}`)
    }

    return data.feedback
  }

  const getFeedbackAttachmentDownloadUrl = async (feedbackId: string, s3Key: string): Promise<string> => {
    if (!token) {
      throw new Error('Not authenticated')
    }

    const response = await fetch(
      `${API_BASE_URL}/admin/feedback/${feedbackId}/attachments/download-url?s3Key=${encodeURIComponent(s3Key)}`,
      { headers: authHeaders() },
    )
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data?.error || `Failed to get download link: ${response.status}`)
    }

    return data.downloadUrl
  }

  return { fetchTenants, fetchFeedback, updateFeedback, getFeedbackAttachmentDownloadUrl }
}
